import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, CogIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { FileStatusType, Small } from "@fronterahealth/frontera-ui-components";

import { ApiLearnerFileProcessingFailureCodeChoices } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";

interface FileStatusDisplayProps {
  fileStatus: FileStatusType;
  failureReason?: string;
  failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
}

export const FileStatusDisplay: React.FC<FileStatusDisplayProps> = ({ fileStatus, failureReason, failureCode }) => {
  const [displayedStatus, setDisplayedStatus] = useState(fileStatus);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (displayedStatus !== fileStatus) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        setDisplayedStatus(fileStatus);
        setIsExiting(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fileStatus]);

  const content = () => {
    switch (displayedStatus) {
      case "pending":
        return (
          <>
            <ExclamationCircleIcon className="size-5 text-yellow-700" />
            <Small className="text-yellow-700">Pending</Small>
          </>
        );
      case "uploading":
        return (
          <>
            <Loader className="w-5 h-5 mr-0 ml-0 flex-none" />
            <Small>Uploading</Small>
          </>
        );
      case "parsing":
        return (
          <>
            <CogIcon className="size-5 animate-spin-slow text-text-secondary flex-none" />
            <Small>Processing</Small>
          </>
        );
      case "classifying":
        return (
          <>
            <Loader className="w-5 h-5 mr-0 ml-0 flex-none" />
            <Small>Identifying</Small>
          </>
        );
      case "classifying-success":
        return (
          <>
            <CheckCircleIcon className="size-5 text-green-700 flex-none" />
            <Small className="text-green-700">Identified</Small>
          </>
        );
      case "done":
        return (
          <>
            <CheckCircleIcon className="size-5 text-green-700 flex-none" />
            <Small className="text-green-700">Complete</Small>
          </>
        );
      case "ai-generating":
        return (
          <>
            <SparklesIcon className="size-5 animate-pulse text-text-secondary flex-none" />
            <Small className="text-text-secondary">Generating Content</Small>
          </>
        );
      case "ai-generation-failed":
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700 flex-none" />
            <Small className="text-red-700">Error Generating | Failed to generate content</Small>
          </>
        );
      case "uploading-failed-url-retrieval":
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700" />
            <Small className="text-red-700">Error Uploading | Failed to retrieve destination</Small>
          </>
        );
      case "uploading-failed-bucket-upload":
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700 flex-none" />
            <Small className="text-red-700">Error Uploading | Failed to upload file</Small>
          </>
        );
      case "uploading-failed-upload-notification":
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700 flex-none" />
            <Small className="text-red-700">Error Uploading | Failed to notify backend</Small>
          </>
        );
      case "uploading-failed-file-size-exceed":
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700 flex-none" />
            <Small className="text-red-700">Error Uploading | File size exceeds the limit</Small>
          </>
        );
      case "classifying-error":
        return (
          <>
            <ExclamationCircleIcon className="size-5 text-red-700" />
            <Small className="text-red-700">Unable to identify</Small>
          </>
        );
      case "parsing-failed":
        if (failureCode === ApiLearnerFileProcessingFailureCodeChoices.FronteraTemplatePdf) {
          // COPY: pending sign-off
          return (
            <div className="flex items-start gap-x-1">
              <ExclamationTriangleIcon className="size-5 shrink-0 text-text-error-primary flex-none" />
              <div className="flex flex-col gap-y-0.5">
                <Small className="text-text-error-primary">
                  This looks like the Frontera scoring template saved as a PDF.
                </Small>
                <Small className="text-text-secondary">
                  Download it as Excel (.xlsx) from the{" "}
                  <Link to="/downloads" className="text-interactive-primary-default underline">
                    Downloads page
                  </Link>{" "}
                  and upload that file instead.
                </Small>
              </div>
            </div>
          );
        }
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-red-700 flex-none" />
            <Small className="text-red-700">{failureReason || "We could not parse this file"}</Small>
          </>
        );
      default:
        console.error("Should not be here, bad status passed in", fileStatus);
        console.error({ fileStatus });
        return (
          <>
            <ExclamationTriangleIcon className="size-5 text-orange-700" />
            <Small className="text-orange-700">Unclear file status: {fileStatus}</Small>
          </>
        );
    }
  };

  return (
    <label
      data-testid="file-status-label"
      className={`flex items-center gap-x-1 text-sm ${isExiting ? "animate-out-fade-top" : "animate-in-fade-bottom"}`}
    >
      {content()}
    </label>
  );
};
